import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { _storage } from "./localStorage";
import useCookies from "../hooks/useCookies";
import { BASE_URL, BASE_URL_AUTH } from "../utils/consts";
import { useState, useEffect } from "react";
import auth, { logout } from "./auth";

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Error class to specifically handle auth errors
export class AuthError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "AuthError";
  }
}

function apiRequest<Type>(
  url: string,
  axiosOptions?: AxiosRequestConfig
): Promise<AxiosResponse<Type>> {
  return api.request({
    url,
    ...axiosOptions,
  });
}

function getTokenExpirationTime(token: string): number {
  const payload = JSON.parse(atob(token.split(".")[1]));
  return payload.exp * 1000 - Date.now();
}

async function verifyAccessToken(token: string): Promise<{ isValid: boolean; status?: number }> {
  try {
    await axios.post(`${BASE_URL_AUTH}/auth/verify`, {
      token: token
    });
    return { isValid: true };
  } catch (error: any) {
    console.log(error.response?.status)
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    if (error.response) {
      return { isValid: false, status: error.response.status };
    }
    return { isValid: false };
  }
}

async function refreshAccessToken(refreshToken: string): Promise<string> {
  try {
    const response = await axios.post(`${BASE_URL_AUTH}/auth/refresh`, {
      token: refreshToken,
    });
    return response.data.accessToken;
  } catch (error) {
    throw new AuthError("Failed to refresh token", 401);
  }
}

async function useApi<Type>(
  url: string,
  authorizationType: "jwt-auth" | "no-auth" = "no-auth",
  axiosOptions?: AxiosRequestConfig
): Promise<Type> {
  const { get, set } = useCookies();
  let token = get("accessToken");
  const refreshTokenValue = get("refreshToken");

  if (authorizationType === "jwt-auth") {
    if (!token) {
      logout();
      throw new AuthError("No access token available", 401);
    }

    // Verify token with the server
    const verification = await verifyAccessToken(token);

    if (!verification.isValid) {
      // If unauthorized (401), immediately log out and throw auth error
      if (verification.status === 401) {
        logout();
        throw new AuthError("Unauthorized: Session expired", 401);
      }

      // For other errors, try to refresh once
      if (refreshTokenValue) {
        try {
          const newToken = await refreshAccessToken(refreshTokenValue);
          set("accessToken", newToken, 60 * 24 * 7);
          token = newToken;
        } catch (error) {
          logout();
          throw new AuthError("Session expired, please log in again", 401);
        }
      } else {
        logout();
        throw new AuthError("No refresh token available", 401);
      }
    }
  }

  try {
    const headers = {
      ...axiosOptions?.headers,
      Authorization:
        token && authorizationType === "jwt-auth"
          ? `Bearer ${token}`
          : undefined,
      "Content-Type": "application/json",
    };

    console.log("🌐 ======= useApi: ENVIANDO REQUEST =======");
    console.log("🔗 URL:", url);
    console.log("📋 Method:", axiosOptions?.method || "GET");
    console.log("🔑 Auth Type:", authorizationType);
    console.log("📦 Headers:", headers);
    console.log("📤 Request Data:", axiosOptions?.data);
    if (axiosOptions?.data) {
      try {
        console.log("📤 Request Data (parsed):", JSON.parse(axiosOptions.data));
      } catch (e) {
        console.log("📤 Request Data (raw):", axiosOptions.data);
      }
    }

    const response = await axios({
      url,
      method: axiosOptions?.method || "GET",
      ...axiosOptions,
      headers,
    });

    console.log("✅ useApi: RESPUESTA RECIBIDA");
    console.log("   Status:", response.status);
    console.log("   Data:", response.data);
    console.log("🌐 ======= useApi: REQUEST COMPLETADO =======");

    return response.data;
  } catch (error: any) {
    console.error("❌ useApi: ERROR EN REQUEST");
    console.error("   URL:", url);
    console.error("   Error:", error);
    console.error("   Response:", error.response);

    // 404 errors may be expected (e.g., no diagnoses found)
    // Pass them through with more context
    if (error.response?.status === 404) {
      console.warn(`API 404 Not Found: ${url}`);
      const err = new Error();
      err.message = error.response?.data?.message || "Resource not found";
      (err as any).response = error.response;
      throw err;
    }

    console.log("API call error:", error);

    // Check if this is an auth error from the API
    if (error.response && error.response.status === 401) {
      logout();
      throw new AuthError("Unauthorized request", 401);
    }

    throw error;
  }
}

export { apiRequest, useApi };

interface FetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
}

async function fetchApiData<T>(
  url: string,
  options: FetchOptions,
  setData: (data: T | null) => void,
  setError: (error: string | null) => void,
  setLoading: (loading: boolean) => void,
  onAuthError?: () => void
) {
  try {
    setLoading(true);
    const response = await useApi<T>(url, "jwt-auth", {
      method: options.method || "GET",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      data: options.body,
    });
    setError(null);
    setData(response);
    setLoading(false);
  } catch (err: any) {
    if (err instanceof AuthError && err.status === 401 && onAuthError) {
      onAuthError();
    }
    setError(err);
    setLoading(false);
  }
}

export function useGetApiData<T>(url: string, options: FetchOptions = {}, onAuthError?: () => void) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);

  const fetchData = () => {
    // Si la URL es vacía o inválida, no hacer la llamada API
    if (!url || url.trim() === "") {
      setLoading(false);
      setData(null);
      setError(null);
      return;
    }

    fetchApiData<T>(url, options, setData, setError, setLoading, onAuthError);
  };

  useEffect(() => {
    fetchData();
  }, [url]);

  return { data, loading, error, refetch: fetchData };
}

export const apiFetch = async (url: string, method: string, data?: any, onAuthError?: () => void) => {
  const options: AxiosRequestConfig = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    data:
      method === "POST" || method === "PUT" || method === "PATCH"
        ? data
        : undefined,
  };

  try {
    const response = await useApi<any>(url, "jwt-auth", options);
    return { response, error: null };
  } catch (error: any) {
    console.log("Error fetching data:", error);

    if (error instanceof AuthError && error.status === 401 && onAuthError) {
      onAuthError();
    }

    return { response: null, error };
  }
};
